import type { WritableLocationStatus } from '@interfaces/IScheduler';
import type { AreaReportTier } from '../../SchedulerPage/lib/locationStatusLabels';
import type { IMatrixCell } from './progressMatrix';

/** How this user edits area cells (area review stage spec, ruling 6): what a
 *  tap does is `nextAreaAction(status, tier)` — a write, or (a manager on an
 *  area awaiting review) the review; nothing for a `null` tier. */
export interface IAreaEditor {
  tier: AreaReportTier;
  /** Set an area's status (taskId = the column, zoneId). */
  write: (taskId: string, zoneId: string, status: WritableLocationStatus) => void;
  /** Open the review of an area submitted for it. */
  review: (taskId: string, zoneId: string) => void;
}

export interface IMatrixCellProps {
  cell: IMatrixCell | null;
  /** 'floor' = rollup summary row (progress fill); 'area' = leaf row (status fill, default). */
  variant?: 'floor' | 'area';
  /** Crosshair anchors — the row/column this cell belongs to. */
  rowKey?: string;
  colKey?: string;
  /** When set (task columns only), the area status is editable by clicking. */
  editable?: boolean;
  /** The zone id for the edit callback (area cells). */
  zoneId?: string;
  /** How this user edits the area (taskId = colKey, zoneId). */
  areaEditor?: IAreaEditor;
  /** Explicit body-pane grid track (windowed rendering: absoluteColumnIndex + 1
   *  — the body pane holds only data columns; labels are a separate pane). */
  gridColumn?: number;
  /** Explicit body-pane grid track (windowed rendering: absoluteRowIndex + 1 —
   *  the pane has no header track). A cell that stays visible across a window
   *  shift (scroll within the buffer) keeps the same absolute row/column
   *  index, hence the same gridRow/gridColumn — same stability invariant
   *  `columnEquals`/memo already rely on for columns, covering the row axis
   *  too. */
  gridRow?: number;
}

function cellEquals(a: IMatrixCell | null, b: IMatrixCell | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.percent === b.percent && a.status === b.status && a.rtlsCoupled === b.rtlsCoupled;
}

/** Value-equality memo: the matrix builder produces all-new cell OBJECTS on every
 *  rebuild (each edit rebuilds twice), but almost all cells are value-identical —
 *  reference compare would re-render every cell per edit. */
export function matrixCellPropsEqual(prev: IMatrixCellProps, next: IMatrixCellProps): boolean {
  return (
    cellEquals(prev.cell, next.cell) &&
    prev.variant === next.variant &&
    prev.rowKey === next.rowKey &&
    prev.colKey === next.colKey &&
    prev.editable === next.editable &&
    prev.zoneId === next.zoneId &&
    prev.areaEditor === next.areaEditor &&
    prev.gridColumn === next.gridColumn &&
    prev.gridRow === next.gridRow
  );
}
